import { BUTTON_NODE_TYPE } from '@dealstack/frontend/shared/components/EmailTemplates/utils/buttonNode';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Inspector } from '@react-email/editor/ui';
import { useCurrentEditor } from '@tiptap/react';

import { useActiveMarks } from '../../hooks/useActiveMarks';
import { LIQUID_VARIABLE_NODE } from '../../extensions/liquidVariableNodes';
import { VariableProperties } from './VariableProperties';

import { useInspectorPresets } from '../../hooks/useInspectorPresets';
import { BRAND_PRESET_COLORS, withCustomColors } from '../../utils/inspectorColors';
import { closestPresetValue } from '../../utils/inspectorPresets';
import { ButtonProperties } from '../ButtonProperties';
import { AlignRow } from './AlignRow';
import { ColorSwatchRow } from './ColorSwatchRow';
import { InspectorSection } from './InspectorSection';
import { PresetRow } from './PresetRow';

interface Props {
	customColors: string[];
	onCustomColorUsed: (hex: string) => void;
}

const IMAGE_NODE_TYPE = 'image';

export const NodeProperties = ({ customColors, onCustomColorUsed }: Props) => {
	const tMipManage = useTranslationMipManage();
	const presets = useInspectorPresets();
	const { editor } = useCurrentEditor();
	const marks = useActiveMarks(editor);

	return (
		<Inspector.Node>
			{(ctx) => {
				// themeDefaults only reports padding support reliably, so
				// Typography and Border always render.
				const supportsPadding = ctx.themeDefaults.paddingTop !== undefined;
				const isImage = ctx.nodeType === IMAGE_NODE_TYPE;
				const presetColors = withCustomColors(
					ctx.presetColors.length > 0 ? ctx.presetColors : BRAND_PRESET_COLORS,
					customColors
				);

				if (ctx.nodeType === LIQUID_VARIABLE_NODE && editor) {
					return (
						<VariableProperties
							editor={editor}
							name={String(ctx.getAttr('name') ?? '')}
							marks={marks}
							customColors={customColors}
							onCustomColorUsed={onCustomColorUsed}
						/>
					);
				}

				return (
					<>
						{ctx.nodeType === BUTTON_NODE_TYPE && (
							<InspectorSection
								title={tMipManage('email_templates.editor.inspector.button', {
									defaultValue: 'Button',
								})}
							>
								<ButtonProperties ctx={ctx} />
							</InspectorSection>
						)}

						{!isImage && (
							<InspectorSection
								title={tMipManage('email_templates.editor.inspector.typography', {
									defaultValue: 'Typography',
								})}
							>
								<ColorSwatchRow
									label={tMipManage('email_templates.editor.inspector.color', {
										defaultValue: 'Color',
									})}
									value={ctx.getStyle('color')}
									presetColors={presetColors}
									onChange={(value) => ctx.setStyle('color', value)}
									onCustomColorUsed={onCustomColorUsed}
								/>
								<PresetRow
									label={tMipManage(
										'email_templates.editor.inspector.font_size',
										{
											defaultValue: 'Font size',
										}
									)}
									options={presets.fontSize}
									value={closestPresetValue(
										presets.fontSize,
										ctx.getStyle('fontSize')
									)}
									onChange={(value) => ctx.setStyle('fontSize', value)}
								/>
								<PresetRow
									label={tMipManage(
										'email_templates.editor.inspector.line_height',
										{ defaultValue: 'Line height' }
									)}
									options={presets.lineHeight}
									value={closestPresetValue(
										presets.lineHeight,
										ctx.getStyle('lineHeight')
									)}
									onChange={(value) => ctx.setStyle('lineHeight', value)}
								/>
								<AlignRow
									value={String(ctx.getAttr('alignment') ?? 'left')}
									onChange={(value) => ctx.setAttr('alignment', value)}
								/>
							</InspectorSection>
						)}

						{supportsPadding && (
							<InspectorSection
								title={tMipManage('email_templates.editor.inspector.spacing', {
									defaultValue: 'Spacing',
								})}
							>
								<PresetRow
									label={tMipManage('email_templates.editor.inspector.padding', {
										defaultValue: 'Padding',
									})}
									options={presets.spacing}
									value={closestPresetValue(
										presets.spacing,
										ctx.getStyle('paddingTop')
									)}
									// One batch: sequential setStyle calls overwrite each other.
									onChange={(value) =>
										ctx.batchSetStyle([
											{ prop: 'paddingTop', value },
											{ prop: 'paddingRight', value },
											{ prop: 'paddingBottom', value },
											{ prop: 'paddingLeft', value },
										])
									}
								/>
							</InspectorSection>
						)}

						<InspectorSection
							title={tMipManage('email_templates.editor.inspector.background', {
								defaultValue: 'Background',
							})}
						>
							<ColorSwatchRow
								label={tMipManage('email_templates.editor.inspector.color', {
									defaultValue: 'Color',
								})}
								value={ctx.getStyle('backgroundColor')}
								presetColors={presetColors}
								onChange={(value) => ctx.setStyle('backgroundColor', value)}
								onCustomColorUsed={onCustomColorUsed}
								allowTransparent
							/>
						</InspectorSection>

						<InspectorSection
							title={tMipManage('email_templates.editor.inspector.border', {
								defaultValue: 'Border',
							})}
						>
							<PresetRow
								label={tMipManage('email_templates.editor.inspector.border_width', {
									defaultValue: 'Border width',
								})}
								options={presets.borderWidth}
								value={closestPresetValue(
									presets.borderWidth,
									ctx.getStyle('borderWidth')
								)}
								onChange={(value) => ctx.setStyle('borderWidth', value)}
							/>
							<ColorSwatchRow
								label={tMipManage('email_templates.editor.inspector.border_color', {
									defaultValue: 'Border color',
								})}
								value={ctx.getStyle('borderColor')}
								presetColors={presetColors}
								onChange={(value) => ctx.setStyle('borderColor', value)}
								onCustomColorUsed={onCustomColorUsed}
								allowTransparent
							/>
							<PresetRow
								label={tMipManage('email_templates.editor.inspector.rounded', {
									defaultValue: 'Rounded',
								})}
								options={presets.cornerRadius}
								value={closestPresetValue(
									presets.cornerRadius,
									ctx.getStyle('borderRadius')
								)}
								onChange={(value) => ctx.setStyle('borderRadius', value)}
							/>
						</InspectorSection>

						<Inspector.Size {...ctx} />
					</>
				);
			}}
		</Inspector.Node>
	);
};
